import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getCurrentDisclaimer,
  getDisclaimerStatus,
  acceptDisclaimer,
} from '../../api/disclaimer';
import { extractErrorMessage } from '../../api/extractErrorMessage';

const initialState = {
  // Current disclaimer content from API
  current: null,
  currentLoading: false,
  currentLoaded: false,
  currentError: null,

  // User's acceptance status
  status: {
    loading: false,
    loaded: false,
    accepted: false,
    acceptedVersion: null,
    currentVersion: null,
    requiresAcceptance: false,
    reason: null,
    error: null,
  },

  // Acceptance submission state
  accepting: false,
  acceptError: null,

  // Flag to show modal (set by 403 interceptor or status check)
  showModal: false,
};

// Fetch current disclaimer content
export const fetchCurrentDisclaimer = createAsyncThunk(
  'disclaimer/fetchCurrent',
  async (disclaimerType = 'platform', { rejectWithValue }) => {
    try {
      const disclaimer = await getCurrentDisclaimer(disclaimerType);
      return disclaimer;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Failed to fetch disclaimer'));
    }
  }
);

// Fetch user's acceptance status
export const fetchDisclaimerStatus = createAsyncThunk(
  'disclaimer/fetchStatus',
  async (disclaimerType = 'platform', { rejectWithValue }) => {
    try {
      const status = await getDisclaimerStatus(disclaimerType);
      return status;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Failed to fetch disclaimer status'));
    }
  }
);

// Submit disclaimer acceptance
export const submitAcceptance = createAsyncThunk(
  'disclaimer/accept',
  async ({ version, contentHash, disclaimerType = 'platform' }, { rejectWithValue }) => {
    try {
      const acceptance = await acceptDisclaimer(version, contentHash, disclaimerType);
      return acceptance;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Failed to accept disclaimer'));
    }
  }
);

const disclaimerSlice = createSlice({
  name: 'disclaimer',
  initialState,
  reducers: {
    // Reset disclaimer state (e.g., on logout)
    resetDisclaimerState() {
      return initialState;
    },

    // Set acceptance required from 403 response
    setAcceptanceRequired(state, action) {
      state.status.requiresAcceptance = true;
      state.status.accepted = false;
      // Clear cached content to force fresh fetch of current version
      state.current = null;
      state.currentLoaded = false;
      state.currentError = null;
      if (action.payload?.requiredVersion) {
        state.status.currentVersion = action.payload.requiredVersion;
      }
      if (action.payload?.acceptedVersion) {
        state.status.acceptedVersion = action.payload.acceptedVersion;
      }
      if (action.payload?.reason) {
        state.status.reason = action.payload.reason;
      }
      state.showModal = true;
    },

    // Clear all errors (for retry functionality)
    clearDisclaimerErrors(state) {
      state.currentError = null;
      state.currentLoaded = false;
      state.acceptError = null;
      state.status.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch current disclaimer
      .addCase(fetchCurrentDisclaimer.pending, (state) => {
        state.currentLoading = true;
        state.currentError = null;
      })
      .addCase(fetchCurrentDisclaimer.fulfilled, (state, action) => {
        state.currentLoading = false;
        state.currentLoaded = true;
        state.current = action.payload;
      })
      .addCase(fetchCurrentDisclaimer.rejected, (state, action) => {
        state.currentLoading = false;
        state.currentError = action.payload || 'Failed to fetch disclaimer';
      })

      // Fetch status
      .addCase(fetchDisclaimerStatus.pending, (state) => {
        state.status.loading = true;
        state.status.error = null;
      })
      .addCase(fetchDisclaimerStatus.fulfilled, (state, action) => {
        state.status.loading = false;
        state.status.loaded = true;
        if (action.payload) {
          state.status.accepted = action.payload.accepted;
          state.status.acceptedVersion = action.payload.acceptedVersion;
          state.status.currentVersion = action.payload.currentVersion;
          state.status.requiresAcceptance = action.payload.requiresAcceptance;
          state.status.reason = action.payload.reason;

          // Show modal if acceptance is required
          if (action.payload.requiresAcceptance) {
            // Clear cached content if it doesn't match the required version
            // This ensures fresh content is fetched for the new version
            if (state.current?.version !== action.payload.currentVersion) {
              state.current = null;
              state.currentLoaded = false;
              state.currentError = null;
            }
            state.showModal = true;
          }
        }
      })
      .addCase(fetchDisclaimerStatus.rejected, (state, action) => {
        state.status.loading = false;
        state.status.loaded = true;
        state.status.error = action.payload || 'Failed to fetch status';
      })

      // Submit acceptance
      .addCase(submitAcceptance.pending, (state) => {
        state.accepting = true;
        state.acceptError = null;
      })
      .addCase(submitAcceptance.fulfilled, (state, action) => {
        state.accepting = false;
        state.status.accepted = true;
        state.status.requiresAcceptance = false;
        state.status.acceptedVersion = action.payload?.disclaimerVersion || state.status.currentVersion;
        state.showModal = false;
      })
      .addCase(submitAcceptance.rejected, (state, action) => {
        state.accepting = false;
        state.acceptError = action.payload || 'Failed to accept disclaimer';
      });
  },
});

export const {
  resetDisclaimerState,
  setAcceptanceRequired,
  clearDisclaimerErrors,
} = disclaimerSlice.actions;

export default disclaimerSlice.reducer;
